import React, { useEffect, useRef, useState } from 'react';
import { OffClickWrapper } from 'radiance-ui';

import { CART_SLIDE_DURATION_MS } from './shared/animations';
import { CartContents } from '..';
import { ActivatedBackground, CartSidebar } from './components';
import { useFloatingElementsShift } from './hooks';

const CART_SIDEBAR_SELECTOR = '[data-testid="shopping-cart-sidebar"]';
const DECAGON_IFRAME_ID = 'decagon-iframe';
const FULLSCREEN_TOLERANCE_PX = 4;

export const CartModal = ({ onClose }: { onClose: () => void }) => {
  const [isOpening, setIsOpening] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const scrollLockRef = useRef<{
    scrollY: number;
    bodyStyles: {
      position: string;
      top: string;
      left: string;
      right: string;
      width: string;
      overflowY: string;
      paddingRight: string;
    };
    htmlOverflow: string;
  } | null>(null);

  // Shift floating elements (chat widgets, etc.) to prevent overlap with cart sidebar
  // Pass isClosing state to synchronize restoration with cart closing animation
  useFloatingElementsShift(isClosing);

  useEffect(() => {
    const timer = setTimeout(() => setIsOpening(false), CART_SLIDE_DURATION_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const { body, documentElement } = document;
    const scrollY = window.scrollY || 0;
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    scrollLockRef.current = {
      scrollY,
      bodyStyles: {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflowY: body.style.overflowY,
        paddingRight: body.style.paddingRight,
      },
      htmlOverflow: documentElement.style.overflow,
    };

    body.dataset.cartOpen = 'true';
    documentElement.dataset.cartOpen = 'true';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflowY = 'scroll';
    body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : '';
    documentElement.style.overflow = 'hidden';

    return () => {
      if (!scrollLockRef.current) {
        return;
      }
      const { scrollY, bodyStyles, htmlOverflow } = scrollLockRef.current;
      body.style.position = bodyStyles.position;
      body.style.top = bodyStyles.top;
      body.style.left = bodyStyles.left;
      body.style.right = bodyStyles.right;
      body.style.width = bodyStyles.width;
      body.style.overflowY = bodyStyles.overflowY;
      body.style.paddingRight = bodyStyles.paddingRight;
      documentElement.style.overflow = htmlOverflow;
      delete body.dataset.cartOpen;
      delete documentElement.dataset.cartOpen;
      window.scrollTo(0, scrollY);
      scrollLockRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const getDecagonIframe = () =>
      document.getElementById(DECAGON_IFRAME_ID) as HTMLElement | null;

    const sidebar = document.querySelector(
      CART_SIDEBAR_SELECTOR,
    ) as HTMLElement | null;

    if (!sidebar) {
      return undefined;
    }

    const toggleDecagon = () => {
      const iframe = getDecagonIframe();

      if (!iframe) {
        return;
      }

      const { width } = sidebar.getBoundingClientRect();
      const shouldHide = width >= window.innerWidth - FULLSCREEN_TOLERANCE_PX;

      iframe.style.transition = 'opacity 200ms ease-in-out';

      if (shouldHide) {
        iframe.style.opacity = '0';
        iframe.style.pointerEvents = 'none';
      } else {
        iframe.style.opacity = '';
        iframe.style.pointerEvents = '';
      }
    };

    toggleDecagon();

    const resizeObserver =
      'ResizeObserver' in window
        ? new ResizeObserver(() => toggleDecagon())
        : undefined;

    resizeObserver?.observe(sidebar);
    window.addEventListener('resize', toggleDecagon);

    return () => {
      window.removeEventListener('resize', toggleDecagon);
      resizeObserver?.disconnect();
      const iframe = getDecagonIframe();
      if (iframe) {
        iframe.style.opacity = '';
        iframe.style.pointerEvents = '';
      }
    };
  }, []);

  const closeCart = () => {
    setIsClosing(true);
    setTimeout(onClose, CART_SLIDE_DURATION_MS);
  };

  return (
    <ActivatedBackground isClosing={isClosing} data-cart-modal="open">
      <OffClickWrapper onOffClick={closeCart}>
        <CartSidebar isClosing={isClosing}>
          <CartContents onClose={closeCart} isOpening={isOpening} />
        </CartSidebar>
      </OffClickWrapper>
    </ActivatedBackground>
  );
};
